import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Header4SmallStyles, BodyLargeStyles, Header3SmallStyles, BodyBoldLargeStyles } from 'web-ui-kit/typography';
import { grayLightM, backgroundLightM, backgroundGrayLightM, successM, surfaceM } from 'web-ui-kit/colors';
import PrimaryButton from 'web-ui-kit/buttons/PrimaryButton';
import SecondaryButton from 'web-ui-kit/buttons/SecondaryButton';
import TieredPromo from 'web-ui-kit/banners/TieredPromo';
import CartItem, { styles as styles$1 } from 'web-ui-kit/CartItem';
import SimpleCheckmark from 'web-ui-kit/assets/SimpleCheckmark';
import { MQ_FOR_TABLET_LANDSCAPE_UP, MQ_FOR_TABLET_LANDSCAPE_BELOW } from 'web-ui-kit/media';
import { toUSDString } from 'web-ui-kit/styleUtils';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _taggedTemplateLiteral(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  if (typeof Symbol === "undefined" || !(Symbol.iterator in Object(arr))) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(n);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8, _templateObject9, _templateObject10, _templateObject11, _templateObject12, _templateObject13, _templateObject14, _templateObject15, _templateObject16, _templateObject17, _templateObject18, _templateObject19;
/**
 *  This component represents the inner content of the cart popup only. Implementation of state
 *  to show/hide, as well as animations to fade in/out are left to a parent component
 *  For UI that is expected on hover state, no variant needs to be provided.
 *  For UI that is triggered on items being added to the cart, the 'items-added' variant must be provided.
 */


var VARIANTS = ['hover', 'items-added'];

function CartPopup(_ref) {
  var _ref$className = _ref.className,
      _ref$dataTestId = _ref.dataTestId,
      dataTestId = _ref$dataTestId === void 0 ? 'cart-popup' : _ref$dataTestId,
      _ref$dispatch = _ref.dispatch,
      dispatch = _ref$dispatch === void 0 ? function () {} : _ref$dispatch,
      _ref$isUpdating = _ref.isUpdating,
      isUpdating = _ref$isUpdating === void 0 ? false : _ref$isUpdating,
      _ref$products = _ref.products,
      products = _ref$products === void 0 ? [] : _ref$products,
      _ref$subtotal = _ref.subtotal,
      subtotal = _ref$subtotal === void 0 ? 0.0 : _ref$subtotal,
      _ref$tieredPromoEnabl = _ref.tieredPromoEnabled,
      tieredPromoEnabled = _ref$tieredPromoEnabl === void 0 ? false : _ref$tieredPromoEnabl,
      _ref$upgrades = _ref.upgrades,
      upgrades = _ref$upgrades === void 0 ? [] : _ref$upgrades,
      _ref$upgradesSubtotal = _ref.upgradesSubtotal,
      upgradesSubtotal = _ref$upgradesSubtotal === void 0 ? 0.0 : _ref$upgradesSubtotal,
      _ref$variant = _ref.variant,
      variant = _ref$variant === void 0 ? 'hover' : _ref$variant;
  var Buttons = styles.Buttons,
      CartSummarySpacer = styles.CartSummarySpacer,
      DiscountAmount = styles.DiscountAmount,
      DiscountLabel = styles.DiscountLabel,
      DiscountLine = styles.DiscountLine,
      EmptyCartMessage = styles.EmptyCartMessage,
      HorizontalRule = styles.HorizontalRule,
      ItemCountHeader = styles.ItemCountHeader,
      ItemList = styles.ItemList,
      StyledSecondaryButton = styles.StyledSecondaryButton,
      SubtotalAmount = styles.SubtotalAmount,
      SubtotalLabel = styles.SubtotalLabel,
      SubtotalLine = styles.SubtotalLine,
      SuccessHeader = styles.SuccessHeader,
      Wrapper = styles.Wrapper;

  var _useState = useState(0),
      _useState2 = _slicedToArray(_useState, 2),
      discount = _useState2[0],
      setDiscount = _useState2[1];

  useEffect(function () {
    savingsInfo.forEach(function (tier, index) {
      var _savingsInfo;

      if (subtotal >= savingsInfo[index].savingsGoal && subtotal < (((_savingsInfo = savingsInfo[index + 1]) === null || _savingsInfo === void 0 ? void 0 : _savingsInfo.savingsGoal) || Number.MAX_SAFE_INTEGER)) {
        setDiscount(savingsInfo[index].savings);
      }
    });
  }, [subtotal]);
  var productItemCount = products.length;

  var renderItemCountTitle = function renderItemCountTitle() {
    return (
      /*#__PURE__*/
      React.createElement(ItemCountHeader, null, copy.itemCountTitle(productItemCount))
    );
  };

  var renderSuccessTitle = function renderSuccessTitle() {
    return (
      /*#__PURE__*/
      React.createElement(SuccessHeader, null,
      /*#__PURE__*/
      React.createElement(SimpleCheckmark, null), copy.successTitle)
    );
  };

  var tieredPromoWrapper = function tieredPromoWrapper() {
    return (
      /*#__PURE__*/
      React.createElement(React.Fragment, null,
      /*#__PURE__*/
      React.createElement(TieredPromo, {
        subtotal: subtotal,
        variant: 'default'
      }),
      /*#__PURE__*/
      React.createElement(HorizontalRule, null))
    );
  };

  var upgradeItemCount = upgrades.length;
  var CART_STATUS = 'PRODUCTS_ONLY';

  if (productItemCount === 0 && upgradeItemCount === 0) {
    CART_STATUS = 'EMPTY';
  } else if (variant === 'hover' && productItemCount > 0 && upgradeItemCount > 0) {
    CART_STATUS = 'PRODUCTS_AND_UPGRADES';
  } else if (upgradeItemCount > 0) {
    CART_STATUS = 'UPGRADES_ONLY';
  }

  if (CART_STATUS === 'EMPTY') {
    return (
      /*#__PURE__*/
      React.createElement(Wrapper, null, renderItemCountTitle(), tieredPromoEnabled && tieredPromoWrapper(),
      /*#__PURE__*/
      React.createElement(EmptyCartMessage, null, "You have no items in your cart."))
    );
  }

  if (CART_STATUS === 'PRODUCTS_AND_UPGRADES') {
    return (
      /*#__PURE__*/
      React.createElement(Wrapper, {
        "data-testid": dataTestId,
        tieredPromo: false,
        variant: "cartSummaries"
      },
      /*#__PURE__*/
      React.createElement(ItemCountHeader, null, copy.multiCartTitle),
      /*#__PURE__*/
      React.createElement(CartSummary, {
        ctaLabel: copy.primaryCtaLabelUpgrades,
        dispatch: dispatch,
        id: "upgrades-cart",
        isUpdating: isUpdating,
        items: upgrades,
        subtotal: upgradesSubtotal,
        title: "Design services"
      }),
      /*#__PURE__*/
      React.createElement(CartSummarySpacer, null),
      /*#__PURE__*/
      React.createElement(CartSummary, {
        ctaLabel: copy.primaryCtaLabelProducts,
        dispatch: dispatch,
        id: "products-cart",
        isUpdating: isUpdating,
        items: products,
        subtotal: subtotal,
        title: "Items"
      }))
    );
  }

  if (CART_STATUS === 'UPGRADES_ONLY') {
    // If the cart only contains upgrades, we can just alias the upgrades data
    // to match the expected products data as the UI is the same.
    tieredPromoEnabled = false;
    products = upgrades;
    subtotal = upgradesSubtotal;
  }

  return (
    /*#__PURE__*/
    React.createElement(Wrapper, {
      "data-testid": dataTestId,
      tieredPromo: tieredPromoEnabled
    }, variant === 'hover' && renderItemCountTitle(), tieredPromoEnabled && tieredPromoWrapper(), variant === 'items-added' && renderSuccessTitle(),
    /*#__PURE__*/
    React.createElement(ItemList, null, products.map(function (product) {
      return (
        /*#__PURE__*/
        React.createElement(CartItem, _extends({
          key: product.name,
          CART_STATUS: CART_STATUS,
          dispatch: dispatch,
          isUpdating: isUpdating,
          productId: product.id,
          variant: variant
        }, product))
      );
    })), tieredPromoEnabled && discount > 0 &&
    /*#__PURE__*/
    React.createElement(DiscountLine, null,
    /*#__PURE__*/
    React.createElement(DiscountLabel, null, "Discounts"),
    /*#__PURE__*/
    React.createElement(DiscountAmount, null, "$", discount, ".00")),
    /*#__PURE__*/
    React.createElement(SubtotalLine, null,
    /*#__PURE__*/
    React.createElement(SubtotalLabel, null, "Cart Subtotal"),
    /*#__PURE__*/
    React.createElement(SubtotalAmount, null, isUpdating ? 'Calculating' : subtotal.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD'
    }))),
    /*#__PURE__*/
    React.createElement(Buttons, null,
    /*#__PURE__*/
    React.createElement(PrimaryButton, {
      disabled: isUpdating,
      dispatch: dispatch,
      fluid: true,
      id: "cart-popup-checkout",
      labelText: copy.primaryCtaLabel,
      size: "medium"
    }), variant === 'items-added' &&
    /*#__PURE__*/
    React.createElement(StyledSecondaryButton, {
      disabled: isUpdating,
      dispatch: dispatch,
      fluid: true,
      id: "cart-popup-continue-shopping",
      labelText: copy.secondaryCtaLabel,
      size: "medium"
    })))
  );
}

CartPopup.propTypes = {
  /** allows style override */
  className: PropTypes.string,

  /** dispatch callback */
  dispatch: PropTypes.func,

  /** allows for loading state when changes are being made */
  isUpdating: PropTypes.bool,

  /** list of products to display */
  products: PropTypes.arrayOf(PropTypes.shape({
    heroImg: PropTypes.string,

    /* Full URL to the primary product image */
    name: PropTypes.string,
    regularPrice: PropTypes.number,
    salePrice: PropTypes.number,
    source: PropTypes.string,
    quantity: PropTypes.number
  })),

  /** numeric value of product cart items */
  subtotal: PropTypes.number,

  /** list of upgrades to display */
  upgrades: PropTypes.arrayOf(PropTypes.shape({
    heroImg: PropTypes.string,

    /* Full URL to the primary product image */
    name: PropTypes.string,
    regularPrice: PropTypes.number,
    salePrice: PropTypes.number
  })),

  /** numeric value of upgrade cart items */
  upgradesSubtotal: PropTypes.number,

  /** used for style/behavior variants */
  variant: PropTypes.oneOf(VARIANTS)
};
/** Renders the cart summary component used when there are multiple carts. */

function CartSummary(_ref2) {
  var _ref2$ctaLabel = _ref2.ctaLabel,
      ctaLabel = _ref2$ctaLabel === void 0 ? '' : _ref2$ctaLabel,
      _ref2$dispatch = _ref2.dispatch,
      dispatch = _ref2$dispatch === void 0 ? function () {} : _ref2$dispatch,
      _ref2$id = _ref2.id,
      id = _ref2$id === void 0 ? '' : _ref2$id,
      _ref2$isUpdating = _ref2.isUpdating,
      isUpdating = _ref2$isUpdating === void 0 ? false : _ref2$isUpdating,
      _ref2$items = _ref2.items,
      items = _ref2$items === void 0 ? [] : _ref2$items,
      _ref2$subtotal = _ref2.subtotal,
      subtotal = _ref2$subtotal === void 0 ? 0.0 : _ref2$subtotal,
      _ref2$title = _ref2.title,
      title = _ref2$title === void 0 ? '' : _ref2$title;
  var Buttons = styles.Buttons,
      CartSummaryImageGrid = styles.CartSummaryImageGrid,
      CartSummaryProductImage = styles.CartSummaryProductImage,
      CartSummaryProductImageContainer = styles.CartSummaryProductImageContainer,
      CartSummaryTitle = styles.CartSummaryTitle,
      SubtotalLine = styles.SubtotalLine,
      SubtotalLabel = styles.SubtotalLabel,
      SubtotalAmount = styles.SubtotalAmount;
  return (
    /*#__PURE__*/
    React.createElement("section", {
      "data-testid": id
    },
    /*#__PURE__*/
    React.createElement(CartSummaryTitle, null, "".concat(title, " (").concat(items.length, ")")),
    /*#__PURE__*/
    React.createElement(CartSummaryImageGrid, null, items.map(function (item) {
      return (
        /*#__PURE__*/
        React.createElement(CartSummaryProductImageContainer, {
          key: item === null || item === void 0 ? void 0 : item.name
        },
        /*#__PURE__*/
        React.createElement(CartSummaryProductImage, {
          alt: item === null || item === void 0 ? void 0 : item.name,
          bucketName: "modsy",
          src: item === null || item === void 0 ? void 0 : item.heroImg,
          height: 72,
          width: 72
        }))
      );
    })),
    /*#__PURE__*/
    React.createElement(SubtotalLine, null,
    /*#__PURE__*/
    React.createElement(SubtotalLabel, null, "Cart Subtotal"),
    /*#__PURE__*/
    React.createElement(SubtotalAmount, null, isUpdating ? 'Calculating' : toUSDString(subtotal))),
    /*#__PURE__*/
    React.createElement(Buttons, null,
    /*#__PURE__*/
    React.createElement(PrimaryButton, {
      disabled: isUpdating,
      dispatch: dispatch,
      fluid: true,
      id: "cart-popup-".concat(id, "-checkout"),
      labelText: ctaLabel,
      size: "medium"
    })))
  );
}

var copy = {
  itemCountTitle: function itemCountTitle(count) {
    return "Your items ".concat(count > 0 ? "(".concat(count, ")") : '');
  },
  multiCartTitle: 'Your carts',
  primaryCtaLabel: 'Go to cart',
  primaryCtaLabelProducts: 'Go to Shopping Cart',
  primaryCtaLabelUpgrades: 'Go to Design Services Cart',
  secondaryCtaLabel: 'Continue Shopping',
  successTitle: "Added to your cart"
}; // ToDo: replace with actual savings and goals.

var savingsInfo = [{
  savings: 150,
  savingsGoal: 1500
}, {
  savings: 350,
  savingsGoal: 3500
}, {
  savings: 750,
  savingsGoal: 7500
}, {
  savings: 1500,
  savingsGoal: 15000
}, {
  savings: 2500,
  savingsGoal: 25000
}];
var styles = {
  Buttons: styled.div(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    display: flex;\n    flex-direction: column;\n\n    > button {\n      margin-bottom: 0.55rem;\n    }\n  "]))),
  CartSummaryImageGrid: styled.div(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    display: grid;\n    grid-template-columns: 1fr 1fr 1fr 1fr;\n    grid-column-gap: 0.25rem;\n    grid-row-gap: 0.25rem;\n    margin-bottom: 1rem;\n  "]))),
  CartSummaryProductImage: styled(styles$1.ProductImage)(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    border: 0.5px solid ", ";\n  "])), grayLightM),
  CartSummaryProductImageContainer: styled(styles$1.ProductImageContainer)(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n  "]))),
  CartSummaryTitle: styled.h4(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n    ", ";\n    margin: 1rem 0;\n  "])), Header4SmallStyles),
  CartSummarySpacer: styled.div(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n    background-color: ", ";\n    flex-shrink: 0;\n    height: 0.5rem;\n    margin: 0.75rem 0 0.25rem 0;\n    width: 100%;\n  "])), backgroundLightM),
  EmptyCartMessage: styled.div(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n    ", ";\n    margin-top: 0.875rem;\n  "])), BodyLargeStyles),
  HorizontalRule: styled.div(_templateObject8 || (_templateObject8 = _taggedTemplateLiteral(["\n    background-color: ", ";\n    display: list-item;\n    height: 0.25rem;\n    margin-left: -1.25rem;\n    margin-top: 0.75rem;\n    width: calc(100% + 2.5rem);\n  "])), backgroundGrayLightM),
  ItemCountHeader: styled.div(_templateObject9 || (_templateObject9 = _taggedTemplateLiteral(["\n    ", ";\n    margin-top: 1.25rem;\n  "])), Header3SmallStyles),
  ItemList: styled.div(_templateObject10 || (_templateObject10 = _taggedTemplateLiteral(["\n    overflow-y: scroll;\n    margin-top: 1rem;\n  "]))),
  StyledSecondaryButton: styled(SecondaryButton)(_templateObject11 || (_templateObject11 = _taggedTemplateLiteral(["\n    ", " {\n      display: none;\n    }\n  "])), MQ_FOR_TABLET_LANDSCAPE_UP),
  DiscountAmount: styled.span(_templateObject12 || (_templateObject12 = _taggedTemplateLiteral(["\n    ", ";\n    color: ", "\n  "])), BodyLargeStyles, successM),
  DiscountLabel: styled.span(_templateObject13 || (_templateObject13 = _taggedTemplateLiteral(["\n    ", ";\n  "])), BodyLargeStyles),
  DiscountLine: styled.div(_templateObject14 || (_templateObject14 = _taggedTemplateLiteral(["\n    display: flex;\n    justify-content: space-between;\n    padding: 1rem 0;\n  "]))),
  SubtotalAmount: styled.span(_templateObject15 || (_templateObject15 = _taggedTemplateLiteral(["\n    ", ";\n  "])), BodyLargeStyles),
  SubtotalLabel: styled.span(_templateObject16 || (_templateObject16 = _taggedTemplateLiteral(["\n    ", ";\n  "])), BodyLargeStyles),
  SubtotalLine: styled.div(_templateObject17 || (_templateObject17 = _taggedTemplateLiteral(["\n    border-top: 1px solid ", ";\n    display: flex;\n    justify-content: space-between;\n    padding: 1rem 0;\n  "])), grayLightM),
  SuccessHeader: styled.div(_templateObject18 || (_templateObject18 = _taggedTemplateLiteral(["\n    ", ";\n    color: ", ";\n    margin-top: 1rem;\n\n    > svg {\n      margin-right: 0.4rem;\n    }\n  "])), BodyBoldLargeStyles, successM),
  Wrapper: styled.div(_templateObject19 || (_templateObject19 = _taggedTemplateLiteral(["\n    padding: 0 1.25rem 1.25rem;\n    display: flex;\n    flex-direction: column;\n    max-width: 100%;\n    background-color: ", ";\n    max-height: calc(70vh - 75px);\n    overflow-x: hidden;\n\n    ", "\n\n    ", " {\n      width: 21.25rem;\n    }\n\n    ", "\n  "])), surfaceM, function (p) {
    return p.tieredPromo && "\n      ".concat(MQ_FOR_TABLET_LANDSCAPE_BELOW, " {\n        padding: 2rem 1.25rem 1.25rem;\n      }\n    ");
  }, MQ_FOR_TABLET_LANDSCAPE_UP, function (p) {
    return p.variant === 'cartSummaries' && "\n      max-height: 70vh;\n    ";
  })
};
export default CartPopup;